/**
 * The customisable dashboard's layout: which cards, in what order, at what size, showing what.
 *
 * ── What is stored ──
 *
 * An ordered list of `BoardLayoutItem`, one per card id the browser has EVER seen — not only the
 * cards on screen now. The catalogue a dashboard offers moves under it: a model is detached and
 * its card goes, a permission is granted and three appear. Dropping a stored entry the moment its
 * card is unavailable would throw away where the operator put it, so the next time the model is
 * attached the card would come back at the end, at the default size, as if never arranged.
 *
 * Cards the catalogue has and the store has not are appended in catalogue order, which is how a
 * brand-new card (or a first visit) lands in the designed default order.
 *
 * ── Where ──
 *
 * localStorage, per browser, versioned in the key. Every read and write is wrapped: a private
 * window or blocked site data throws on access, and a dashboard must render its default layout
 * rather than fail because it cannot remember a preference.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type BoardSize = 'shrink' | 'expand';
export type BoardView = 'list' | 'cards';
/** How wide an EXPANDED card is on a wide board: two thirds of it, or all of it. */
export type BoardSpan = 'two-thirds' | 'full';
/**
 * Where an EXPANDED card puts its list: beside the summary (one row tall, wider) or under it
 * (a shrunk card's width, two rows tall).
 */
export type BoardOrient = 'horizontal' | 'vertical';
export type BoardRange = 'today' | '24h' | '7d';

export interface BoardLayoutItem {
  id: string;
  size: BoardSize;
  /** How an EXPANDED card shows its details. Ignored while shrunk. */
  view: BoardView;
  /** How wide an EXPANDED card is. Ignored while shrunk, and while vertical. */
  span: BoardSpan;
  /** Where an EXPANDED card's list goes. Ignored while shrunk. */
  orient: BoardOrient;
  /** Captions under charts and the metadata footer. */
  showMeta: boolean;
  /** The window the card's figures count over — only for cards that support one. */
  range: BoardRange;
  hidden: boolean;
}

export interface BoardCatalogEntry {
  id: string;
  /** The details view a card opens in when first expanded. Defaults to `list`. */
  defaultView?: BoardView;
}

export const BOARD_RANGES: Array<{ value: BoardRange; label: string }> = [
  { value: 'today', label: 'Today' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
];

const SIZES: BoardSize[] = ['shrink', 'expand'];
const VIEWS: BoardView[] = ['list', 'cards'];
const SPANS: BoardSpan[] = ['two-thirds', 'full'];
const ORIENTS: BoardOrient[] = ['horizontal', 'vertical'];
const RANGES = BOARD_RANGES.map((r) => r.value);

function defaultItem(entry: BoardCatalogEntry): BoardLayoutItem {
  return {
    id: entry.id,
    size: 'shrink',
    view: entry.defaultView || 'list',
    span: 'two-thirds',
    orient: 'horizontal',
    showMeta: true,
    range: 'today',
    hidden: false,
  };
}

/** A stored entry, field by field — anything malformed falls back rather than poisoning the grid. */
function clean(raw: any): BoardLayoutItem | null {
  if (!raw || typeof raw.id !== 'string' || !raw.id) return null;
  return {
    id: raw.id,
    size: SIZES.includes(raw.size) ? raw.size : 'shrink',
    view: VIEWS.includes(raw.view) ? raw.view : 'list',
    span: SPANS.includes(raw.span) ? raw.span : 'two-thirds',
    orient: ORIENTS.includes(raw.orient) ? raw.orient : 'horizontal',
    showMeta: raw.showMeta !== false,
    range: RANGES.includes(raw.range) ? raw.range : 'today',
    hidden: raw.hidden === true,
  };
}

function readStored(key: string): BoardLayoutItem[] {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const seen = new Set<string>();
    return parsed
      .map(clean)
      .filter((it): it is BoardLayoutItem => !!it && !seen.has(it.id) && !!seen.add(it.id));
  } catch {
    return [];
  }
}

function writeStored(key: string, items: BoardLayoutItem[] | null) {
  try {
    if (items === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(items));
  } catch {
    /* Remembering the layout is a convenience; failing to is not an error worth surfacing. */
  }
}

export interface BoardLayout {
  /** The catalogue's cards in layout order, hidden ones included (they carry `hidden: true`). */
  items: BoardLayoutItem[];
  update: (id: string, patch: Partial<Omit<BoardLayoutItem, 'id'>>) => void;
  /** Swap with the previous (-1) or next (+1) VISIBLE card. */
  move: (id: string, delta: -1 | 1) => void;
  /** Drag and drop: put `dragId` where `overId` is. */
  moveTo: (dragId: string, overId: string) => void;
  reset: () => void;
  /**
   * Customize mode is a DRAFT: after `beginEdit`, changes show on the board but are not stored
   * until `saveEdit`; `cancelEdit` puts back the layout as it was when editing began. Outside
   * a draft (a card's own view toggle) changes are stored straight away, as before.
   */
  beginEdit: () => void;
  saveEdit: () => void;
  cancelEdit: () => void;
}

export function useBoardLayout(storageKey: string, catalog: BoardCatalogEntry[]): BoardLayout {
  const [stored, setStored] = useState<BoardLayoutItem[]>(() => readStored(storageKey));
  /** The layout as it was when Customize opened; non-null = a draft is in progress. */
  const draftFrom = useRef<BoardLayoutItem[] | null>(null);

  useEffect(() => {
    draftFrom.current = null;
    setStored(readStored(storageKey));
  }, [storageKey]);

  const catalogKey = catalog.map((c) => `${c.id}:${c.defaultView || ''}`).join('|');

  /** Stored entries plus anything the store has not seen yet, in catalogue order. */
  const full = useMemo(() => {
    const known = new Set(stored.map((s) => s.id));
    return [...stored, ...catalog.filter((c) => !known.has(c.id)).map(defaultItem)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stored, catalogKey]);

  const items = useMemo(() => {
    const available = new Set(catalog.map((c) => c.id));
    return full.filter((it) => available.has(it.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [full, catalogKey]);

  const commit = useCallback(
    (next: BoardLayoutItem[]) => {
      setStored(next);
      if (!draftFrom.current) writeStored(storageKey, next);
    },
    [storageKey],
  );

  const update = useCallback<BoardLayout['update']>(
    (id, patch) => commit(full.map((it) => (it.id === id ? { ...it, ...patch } : it))),
    [full, commit],
  );

  const moveTo = useCallback<BoardLayout['moveTo']>(
    (dragId, overId) => {
      if (dragId === overId) return;
      const from = full.findIndex((it) => it.id === dragId);
      const to = full.findIndex((it) => it.id === overId);
      if (from < 0 || to < 0) return;
      const next = full.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      commit(next);
    },
    [full, commit],
  );

  const move = useCallback<BoardLayout['move']>(
    (id, delta) => {
      // Neighbours among the cards actually ON the board — stepping past a hidden or
      // unavailable card would look like the button did nothing.
      const visible = items.filter((it) => !it.hidden);
      const at = visible.findIndex((it) => it.id === id);
      const other = visible[at + delta];
      if (at < 0 || !other) return;
      const next = full.slice();
      const a = next.findIndex((it) => it.id === id);
      const b = next.findIndex((it) => it.id === other.id);
      [next[a], next[b]] = [next[b], next[a]];
      commit(next);
    },
    [items, full, commit],
  );

  const reset = useCallback(() => {
    setStored([]);
    if (!draftFrom.current) writeStored(storageKey, null);
  }, [storageKey]);

  const beginEdit = useCallback(() => {
    draftFrom.current = stored;
  }, [stored]);

  const saveEdit = useCallback(() => {
    draftFrom.current = null;
    // An empty list is "the default layout" — stored as no entry, exactly as reset() does.
    writeStored(storageKey, stored.length ? stored : null);
  }, [storageKey, stored]);

  const cancelEdit = useCallback(() => {
    if (draftFrom.current) setStored(draftFrom.current);
    draftFrom.current = null;
  }, []);

  return { items, update, move, moveTo, reset, beginEdit, saveEdit, cancelEdit };
}
